import { Trophy, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { TrustBar } from "@/components/TrustBar";
import { ConsultantCard } from "@/components/ConsultantCard";
import { CtaFinal } from "@/components/CtaFinal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { Footer } from "@/components/Footer";
import { LeadForm } from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/lib/seo";
import { RACING_IMAGES, GALLERY_GARA } from "@/data/content";
import { scrollToId } from "@/lib/smooth";

export default function ChiSiamo() {
  useSeo({
    title: "Chi Siamo: la passione dei motori dietro XMOVE | XMOVE Italia",
    description:
      "XMOVE Italia: noleggio lungo termine multimarca e sponsor in Lamborghini Super Trofeo. La precisione della pista, applicata al tuo contratto.",
    path: "/chi-siamo/",
  });

  return (
    <div className="pb-[72px] md:pb-0" data-testid="chi-siamo-page">
      <Header />
      <main>
        <section className="mx-auto max-w-7xl px-5 py-14 lg:px-8 lg:py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-xmove-orange">
                <Trophy size={15} /> Sponsor Lamborghini Super Trofeo
              </p>
              <h1 className="mt-4 text-balance font-display text-4xl font-bold leading-[1.06] text-xmove-ink sm:text-5xl" data-testid="chi-siamo-h1">
                La precisione della pista, nel tuo contratto di noleggio
              </h1>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-xmove-muted md:text-lg">
                XMOVE nasce da chi i motori li vive davvero. Ogni weekend di gara il nostro nome corre nel
                campionato Lamborghini Super Trofeo: è lì, tra telemetrie e decimi di secondo, che abbiamo
                imparato che vince chi cura i dettagli. Applichiamo la stessa
                ossessione a ogni preventivo: consultiamo lo stock più ampio d'Italia e selezioniamo
                per te l'offerta migliore, con un unico referente dalla prima chiamata alla consegna.
              </p>
              <button
                onClick={() => scrollToId("preventivo")}
                data-testid="chi-siamo-cta-btn"
                className="mt-8 inline-flex items-center gap-2 rounded-xl bg-xmove-orange px-7 py-4 text-sm font-bold text-white shadow-soft transition-all hover:bg-xmove-orangeDark active:scale-[0.98]"
              >
                Parla con il nostro team <ArrowRight size={16} />
              </button>
            </Reveal>
            <div className="grid grid-cols-2 gap-4">
              <Reveal className="col-span-2">
                <img src={RACING_IMAGES[0]} alt="Lamborghini GT3 XMOVE in pista" loading="lazy" decoding="async" className="aspect-[16/9] w-full rounded-2xl object-cover shadow-lift" />
              </Reveal>
              <Reveal delay={0.1}>
                <img src={RACING_IMAGES[1]} alt="Vettura da corsa sponsorizzata XMOVE" loading="lazy" className="aspect-square w-full rounded-2xl object-cover shadow-soft" />
              </Reveal>
              <Reveal delay={0.18}>
                <img src={RACING_IMAGES[2]} alt="Campionato Lamborghini Super Trofeo" loading="lazy" className="aspect-square w-full rounded-2xl object-cover shadow-soft" />
              </Reveal>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 lg:py-20" data-testid="founder-section">
          <div className="mx-auto grid max-w-5xl items-center gap-10 px-5 md:grid-cols-[2fr_3fr] lg:gap-14 lg:px-8">
            <Reveal>
              <div className="relative mx-auto max-w-xs md:max-w-none">
                <div className="absolute -inset-3 rounded-3xl border border-xmove-orange/30" />
                <img
                  src="/images/team-xmove.webp"
                  alt="Il team XMOVE Italia in autodromo durante un weekend di gara"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/5] w-full rounded-2xl object-cover object-top shadow-lift"
                  data-testid="founder-photo"
                />
                <span className="absolute -bottom-4 left-5 rounded-full bg-xmove-orange px-4 py-2 text-xs font-bold uppercase tracking-wider text-white shadow-lift">
                  Il team XMOVE
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="text-xs font-semibold uppercase tracking-widest text-xmove-orange">Una garanzia personale</p>
              <h2 className="mt-3 font-display text-3xl font-bold text-xmove-ink sm:text-4xl">
                "Ogni cliente ha il nostro numero. Se qualcosa non va, ci chiami."
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-xmove-muted">
                XMOVE non è una piattaforma anonima: è una società con una faccia e una responsabilità
                diretta. Ho costruito questo servizio partendo dalla pista, dove ogni dettaglio conta e
                ogni promessa si misura al cronometro. Lo stesso standard lo pretendo su ogni contratto:
                canone chiaro, tempi rispettati, un referente che risponde davvero.
              </p>
              <p className="mt-5 font-display text-lg font-bold text-xmove-ink" data-testid="founder-role">
                Andrea Vecchioni — Amministratore, XMOVE Italia
              </p>
            </Reveal>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20" data-testid="gara-gallery">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-widest text-xmove-orange">Il weekend di gara</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-xmove-ink sm:text-4xl">
              Lamborghini Super Trofeo, dal vivo
            </h2>
            <p className="mt-3 max-w-2xl text-base text-xmove-muted md:text-lg">
              La #252 con i colori XMOVE: box, griglia di partenza e duelli ruota a ruota. Questa è la nostra seconda casa.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-6 md:grid-rows-[repeat(3,220px)] md:gap-4">
            {GALLERY_GARA.map((img, i) => (
              <Reveal key={img.src} delay={i * 0.06} className={img.span}>
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  data-testid={`gara-img-${i}`}
                  className={`w-full rounded-2xl object-cover shadow-soft transition-transform duration-500 hover:scale-[1.02] ${img.aspect}`}
                />
              </Reveal>
            ))}
          </div>
        </section>

        <TrustBar />
        <ConsultantCard racingBg={RACING_IMAGES[2]} />
        <section id="preventivo" className="scroll-mt-24 bg-white py-16 lg:py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-2 lg:px-8">
            <Reveal>
              <h2 className="font-display text-3xl font-bold text-xmove-ink sm:text-4xl">Mettici alla prova</h2>
              <p className="mt-4 max-w-md text-base leading-relaxed text-xmove-muted">
                Mandaci una richiesta e guarda come lavoriamo: selezione dallo stock più ampio d'Italia,
                canone negoziato, consulente dedicato. Il primo passo è gratuito.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="rounded-2xl border border-xmove-ink/8 bg-xmove-cream p-6 shadow-soft sm:p-8">
                <LeadForm idPrefix="chisiamo" compact />
              </div>
            </Reveal>
          </div>
        </section>
        <CtaFinal title="Pronto a partire? Noi siamo già ai box." />
      </main>
      <Footer />
      <MobileStickyBar />
    </div>
  );
}
